import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CONTAINER_TYPE } from 'khufu-legos';
import { CloseButton, Col, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { createProduct } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { PRODUCTS_QUERY_KEY } from './Products';

interface CreateProductsModalProps {
  show: boolean;
  handleClose: () => void;
}

interface CreateProductFormInput {
  name: string;
  code: string;
  description?: string;
  price: number;
  isActive: boolean;
  requiresEmpties: boolean;
  containerType: CONTAINER_TYPE;
}

const CreateProductModal = ({ show, handleClose }: CreateProductsModalProps) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (product: CreateProductFormInput) => {
      return createProduct(product);
    },
    onSuccess: data => {
      const { data: newProduct } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `PRODUCT CREATED`,
        subtitle: `${newProduct?.name} has been added`
      });

      handleClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateProductFormInput>({
    mode: 'all',
    defaultValues: {
      name: '',
      code: '',
      description: '',
      price: 0,
      isActive: true,
      requiresEmpties: false
    }
  });

  const {
    getValues,
    register,
    setValue,
    watch,
    formState: { isValid, errors }
  } = methods;

  const containerType = watch('containerType');
  if (containerType === 'can') {
    setValue('requiresEmpties', false);
  }

  const onCreateProduct = () => {
    const fields = getValues();
    const productData = {
      ...fields,
      description: fields.description && fields.description.trim() ? fields.description : undefined
    };
    mutation?.mutate(productData);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">Add Product</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <Form.Group className="my-4">
            <Form.Check className="fw-bold" type="switch" label={'Activate Product'} {...register('isActive')} />
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Coke 50cl bottle"
              {...register('name', {
                required: 'Product name is required',
                validate: value => value.trim() !== '' || 'Product name cannot be empty',
                pattern: {
                  value: /^[a-zA-Z0-9 ]+$/,
                  message: 'Product name should contain only alphabets and numbers'
                }
              })}
              autoFocus
            />
            {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label>Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="CK50BL"
              {...register('code', {
                required: 'Product code is required',
                validate: value => value.trim() !== '' || 'Product code cannot be empty',
                pattern: {
                  value: /^[a-zA-Z0-9 ]+$/,
                  message: 'Product code should contain only alphabets and numbers'
                }
              })}
              autoFocus
            />
            {errors?.code && <DisplayFieldErrors displayText={errors.code.message!} />}
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              placeholder="Type here"
              {...register('description', {
                validate: value => {
                  if (value === '') {
                    return true;
                  }

                  const trimmedValue = value?.trim();
                  return !!trimmedValue || 'Should not contain only empty spaces';
                },
                pattern: {
                  value: /^[a-zA-Z0-9 ]+$/,
                  message: 'Description should contain only alpha numeric'
                }
              })}
              autoFocus
            />
            {errors?.description && <DisplayFieldErrors displayText={errors.description.message!} />}
          </Form.Group>
          <Form.Group className="my-3">
            <Form.Label htmlFor="containerType">Container Type</Form.Label>
            <Form.Select id="containerType" {...register('containerType', { required: true })}>
              <option value="can">Can</option>
              <option value="bottle">Bottle - Glass / Plastic</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="my-4">
            <Form.Check
              className="fw-bold"
              type="switch"
              label={'Requires Empties'}
              {...register('requiresEmpties')}
              disabled={containerType === 'can'}
            />
          </Form.Group>
          <Form.Group className="my-2">
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              placeholder="100.00"
              step="0.01"
              min="0.01"
              {...register('price', {
                required: true,
                validate: {
                  positive: value => value >= 0.01 || 'Price must be greater than 0',
                  decimals: value => /^\d+(\.\d{1,2})?$/.test(String(value)) || 'Price can only accept 2 decimal places'
                },
                valueAsNumber: true
              })}
              autoFocus
            />
            {errors.price && <DisplayFieldErrors displayText={errors.price.message!} />}
          </Form.Group>
        </Form>
      </ModalComponent.Body>
      <ModalComponent.Footer>
        <Col className="d-grid">
          <SubmitButton buttonText="cancel" variant="white" onClick={handleClose} disabled={loading} loading={false} />
        </Col>
        <Col className="d-grid">
          <SubmitButton
            buttonText="Add Product"
            onClick={onCreateProduct}
            loading={loading}
            disabled={!isValid || loading}
          />
        </Col>
      </ModalComponent.Footer>
    </ModalComponent>
  );
};

export default CreateProductModal;
